import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../Images/SkillsHapLogo.png';

// Hook: Smooth scroll to top on route change
const useSmoothScrollToTopOnNavigation = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);
};

// Helper: Scroll with offset
const scrollWithOffset = (el) => {
  const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
  const yOffset = -80;
  window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
};

export default function Navbar() {
  useSmoothScrollToTopOnNavigation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState({
    products: false,
    solutions: false,
    join: false,
  });

  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const toggleMobileDropdown = (key) => {
    setMobileDropdownOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  useEffect(() => {
    const handleMobileLinkClick = (e) => {
      if (window.innerWidth <= 768 && e.target.closest('.dropdown-content')) {
        setMenuOpen(false);
        setMobileDropdownOpen({ products: false, solutions: false, join: false });
      }
    };
    document.addEventListener('click', handleMobileLinkClick);
    return () => document.removeEventListener('click', handleMobileLinkClick);
  }, []);

  const handleProductClick = (sectionId) => {
    navigate('/');
    setMenuOpen(false);
    setMobileDropdownOpen({ products: false, solutions: false, join: false });
    setTimeout(() => {
      const target = document.getElementById(sectionId);
      if (target) scrollWithOffset(target);
    }, 100);
  };

  return (
    <div>
      {/* Hero Banner */}
      <div className="hero-banner">
        5X Your Job Placements in Just 30 Days — Learn from Real Hiring Managers in Our Free Live Webinars
        <Link to="/RegisterNow"><span className="cta">REGISTER FOR FREE</span></Link>
      </div>

      {/* Navbar */}
      <div className="navbar" ref={dropdownRef}>
        <a href="/" className="logo" onClick={handleLogoClick}>
          <img src={Logo} alt="SkillsHap Logo" />
        </a>

        {/* Mobile demo + hamburger */}
        <div className="navbar-right-mobile">
          <Link to="/BookDemoForm">
            {!menuOpen && <div className="mobile-demo-button">Book A Demo</div>}
          </Link>
          <div className="menu-toggle" onClick={toggleMenu}>
            <span></span><span></span><span></span>
          </div>
        </div>

        {/* Nav Links */}
        <div className={`nav-links ${menuOpen ? 'show' : ''}`}>
          {/* Products */}
          <div
            className="nav-item dropdown"
            onClick={() => window.innerWidth <= 768 && toggleMobileDropdown('products')}
          >
            Products
            <div
              className={`dropdown-content animated ${mobileDropdownOpen.products ? 'show' : ''
                }`}
            >
              {[
                'AI Resume Builder',
                'AI Mock Interview',
                'Mentorship',
                'AI Skill Assignment',
                'ATS Recommendation',
                'Employee Portal',
                'Job Portal',
              ].map((name) => (
                <Link
                  key={name}
                  to="/"
                  className="solution-group hover-highlight"
                  onClick={(e) => {
                    e.preventDefault();
                    handleProductClick('skill-assignment'); // Always scroll to AI Skill Assignment
                  }}
                >
                  <span className="solution-title">{name}</span>
                </Link>
              ))}

            </div>
          </div>

          {/* Solutions */}
          <div
            className="nav-item dropdown"
            onClick={() => window.innerWidth <= 768 && toggleMobileDropdown('solutions')}
          >
            Solutions
            <div
              className={`dropdown-content animated ${mobileDropdownOpen.solutions ? 'show' : ''
                }`}
            >
              <Link to="/Careerpage" className="solution-group hover-highlight" onClick={() => setMenuOpen(false)}>
                <span className="solution-title">Career Coach</span>
                <span className="solution-subtext">Hire and Manage hybrid workforce</span>
              </Link>
              <Link to="/colleges" className="solution-group hover-highlight" onClick={() => setMenuOpen(false)}>
                <span className="solution-title">Colleges & Universities</span>
                <span className="solution-subtext">Find best global opportunities</span>
              </Link>
              <Link to="/RecruiterPage" className="solution-group hover-highlight" onClick={() => setMenuOpen(false)}>
                <span className="solution-title">Recruiter</span>
                <span className="solution-subtext">Multiply your income 3X</span>
              </Link>
              <Link to="/institutes" className="solution-group hover-highlight" onClick={() => setMenuOpen(false)}>
                <span className="solution-title">Training & Institutes</span>
                <span className="solution-subtext">Trusted by Institutes. Loved by Students</span>
              </Link>
            </div>
          </div>

          {/* Join Us */}
          <div
            className="nav-item dropdown"
            onClick={() => window.innerWidth <= 768 && toggleMobileDropdown('join')}
          >
            Join Us
            <div
              className={`dropdown-content animated ${mobileDropdownOpen.join ? 'show' : ''
                }`}
            >
              <Link to="/HrPage" className="solution-group hover-highlight" onClick={() => setMenuOpen(false)}>
                <span className="solution-title">Placement Partners</span>
                <span className="solution-subtext">Strategic support for hiring teams</span>
              </Link>
              <Link to="/Sales" className="solution-group hover-highlight" onClick={() => setMenuOpen(false)}>
                <span className="solution-title">Growth Partners</span>
                <span className="solution-subtext">Driving growth through smart selling</span>
              </Link>
              <Link to="/Coach" className="solution-group hover-highlight" onClick={() => setMenuOpen(false)}>
                <span className="solution-title">Career Expert</span>
                <span className="solution-subtext">Expert guidance for career success</span>
              </Link>
              <Link to="/LearningCollaborators" className="solution-group hover-highlight" onClick={() => setMenuOpen(false)}>
                <span className="solution-title">Learning Collaborators</span>
                <span className="solution-subtext">Practical learning for real jobs</span>
              </Link>
              <Link to="/MockInterviewers" className="solution-group hover-highlight" onClick={() => setMenuOpen(false)}>
                <span className="solution-title">Mock Interviewers</span>
                <span className="solution-subtext">Shape talent with mock interviews</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Auth buttons */}
        <div className={`nav-auth ${menuOpen ? 'show' : ''}`}>
          <Link to="/Login"><button className="login">LOG IN</button></Link>
          <Link to="/SignupForm"><button className="signup">SIGN UP FOR FREE</button></Link>
        </div>
      </div>
    </div>
  );
}